import React, { useEffect, useState, useCallback } from "react";
import { ethers } from "ethers";
import { Wallet, Copy, ExternalLink, RefreshCw, PlusCircle, ArrowDownUp, Settings, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  ERC20_ABI, ROUTER_ABI, ensureBsc, readProvider, walletProvider, injected, shortAddr,
  getInjected, walletDeepLink, WALLET_INSTALL, isMobile,
} from "../lib/web3";

const METAMASK_ICON = "https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg";
const TRUST_ICON = "https://s2.coinmarketcap.com/static/img/coins/64x64/5964.png";
const WALLETS = [
  { id: "metamask", name: "MetaMask", icon: METAMASK_ICON },
  { id: "trust", name: "Trust Wallet", icon: TRUST_ICON },
];

const fmt = (v, d = 4) => {
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return n.toLocaleString("it-IT", { maximumFractionDigits: d });
};

export default function RaeinWallet() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cfg, setCfg] = useState(null);
  const [price, setPrice] = useState(null);
  const [address, setAddress] = useState("");
  const [bnb, setBnb] = useState(null);
  const [tokenBal, setTokenBal] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [eip, setEip] = useState(null);
  const [showConnect, setShowConnect] = useState(false);
  const [bnbAmount, setBnbAmount] = useState("0.05");
  const [slippage, setSlippage] = useState("1");
  const [quote, setQuote] = useState(null);
  const [buying, setBuying] = useState(false);
  const [showCfg, setShowCfg] = useState(false);
  const [cfgForm, setCfgForm] = useState({ contract: "", logo: "", description: "" });

  useEffect(() => {
    api.get("/token/config").then((r) => {
      setCfg(r.data);
      setCfgForm({ contract: r.data.contract || "", logo: r.data.logo || "", description: r.data.description || "" });
    }).catch(() => {});
    api.get("/token/price").then((r) => setPrice(r.data)).catch(() => {});
  }, []);

  const hasContract = cfg?.contract && cfg.contract.length === 42;

  const refresh = useCallback(async (user_addr) => {
    const addr = user_addr || address;
    if (!addr || !hasContract) return;
    try {
      const p = readProvider(cfg.rpc);
      const token = new ethers.Contract(cfg.contract, ERC20_ABI, p);
      const [b, raw] = await Promise.all([p.getBalance(addr), token.balanceOf(addr)]);
      setBnb(ethers.formatEther(b));
      setTokenBal(ethers.formatUnits(raw, cfg.decimals || 18));
    } catch (e) { /* read errors are non-fatal */ }
  }, [address, cfg, hasContract]);

  const loadQuote = useCallback(async () => {
    if (!hasContract || !bnbAmount || Number(bnbAmount) <= 0) { setQuote(null); return; }
    try {
      const p = readProvider(cfg.rpc);
      const router = new ethers.Contract(cfg.router, ROUTER_ABI, p);
      const out = await router.getAmountsOut(ethers.parseEther(bnbAmount), [cfg.wbnb, cfg.contract]);
      setQuote(ethers.formatUnits(out[1], cfg.decimals || 18));
    } catch (e) { setQuote(null); }
  }, [cfg, bnbAmount, hasContract]);

  useEffect(() => { loadQuote(); }, [loadQuote]);

  useEffect(() => {
    if (!window.ethereum) return;
    const changed = () => { setAddress(""); setBnb(null); setTokenBal(null); };
    window.ethereum.on?.("accountsChanged", changed);
    window.ethereum.on?.("chainChanged", changed);
    return () => {
      window.ethereum.removeListener?.("accountsChanged", changed);
      window.ethereum.removeListener?.("chainChanged", changed);
    };
  }, []);

  const connectWith = async (kind) => {
    const provider = getInjected(kind);
    if (!provider) {
      if (isMobile()) {
        window.location.href = walletDeepLink(kind);
      } else {
        toast.error(`${kind === "metamask" ? "MetaMask" : "Trust Wallet"} non rilevato. Installa l'estensione.`);
        window.open(WALLET_INSTALL[kind], "_blank");
      }
      return;
    }
    setConnecting(true);
    try {
      await ensureBsc(provider);
      const wp = walletProvider(provider);
      const accounts = await wp.send("eth_requestAccounts", []);
      const addr = ethers.getAddress(accounts[0]);
      setEip(provider);
      setAddress(addr);
      setShowConnect(false);
      await refresh(addr);
      toast.success("Wallet collegato");
    } catch (e) {
      toast.error(e.shortMessage || e.message || "Connessione al wallet fallita");
    } finally { setConnecting(false); }
  };

  const buy = async () => {
    if (!address) { toast.error("Collega prima il wallet"); return; }
    if (!hasContract) { toast.error("Token non ancora configurato"); return; }
    setBuying(true);
    try {
      await ensureBsc(eip);
      const signer = await walletProvider(eip).getSigner();
      const router = new ethers.Contract(cfg.router, ROUTER_ABI, signer);
      const amountIn = ethers.parseEther(bnbAmount);
      const out = await router.getAmountsOut(amountIn, [cfg.wbnb, cfg.contract]);
      const mult = 10000n - BigInt(Math.round(Number(slippage) * 100));
      const minOut = (out[1] * mult) / 10000n;
      const deadline = Math.floor(Date.now() / 1000) + 600;
      toast.info("Conferma lo swap nel tuo wallet…");
      const tx = await router.swapExactETHForTokens(minOut, [cfg.wbnb, cfg.contract], address, deadline, { value: amountIn });
      toast.info("Transazione inviata, in attesa di conferma…");
      await tx.wait();
      api.post("/token/trades", { tx_hash: tx.hash, amount_bnb: bnbAmount, wallet: address }).catch(() => {});
      toast.success("Acquisto completato!");
      await refresh();
    } catch (e) {
      toast.error(e.shortMessage || e.reason || e.message || "Swap fallito");
    } finally { setBuying(false); }
  };

  const addToWallet = async () => {
    try {
      await (eip || injected()).request({
        method: "wallet_watchAsset",
        params: { type: "ERC20", options: { address: cfg.contract, symbol: cfg.symbol, decimals: Number(cfg.decimals || 18), image: cfg.logo || undefined } },
      });
    } catch (e) { toast.error("Impossibile aggiungere il token"); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copiato"); };

  const saveCfg = async () => {
    try {
      const { data } = await api.put("/admin/token/config", cfgForm);
      setCfg(data);
      toast.success("Configurazione salvata");
      setShowCfg(false);
      api.get("/token/price").then((r) => setPrice(r.data)).catch(() => {});
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (!cfg) return <div className="text-sm text-zinc-500 py-8">Caricamento wallet…</div>;

  const change = price?.available && price.price_change_24h != null ? Number(price.price_change_24h) : null;
  const noMarket = price && !price.available && !/configurat/i.test(price.reason || "");

  return (
    <div className="space-y-5" data-testid="raein-wallet">
      {/* Token header */}
      <div className="bg-gradient-to-br from-amber-500/10 to-black border border-amber-500/20 rounded-xl p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            {cfg.logo
              ? <img src={cfg.logo} alt={cfg.symbol} className="w-12 h-12 rounded-full object-cover" />
              : <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center"><Wallet size={22} className="text-amber-400" /></div>}
            <div>
              <div className="text-xl font-bold text-white">{cfg.name}</div>
              <div className="text-sm font-mono text-amber-400">${cfg.symbol}</div>
            </div>
          </div>
          <div className="text-right">
            {price?.available ? (
              <>
                <div className="text-2xl font-bold text-white" data-testid="raein-price">
                  ${fmt(price.price_usd, 8)}
                </div>
                {change != null && (
                  <div className={`text-sm font-mono flex items-center gap-1 justify-end ${change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {change >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                    {change >= 0 ? "+" : ""}{fmt(change, 2)}% 24h
                  </div>
                )}
              </>
            ) : (
              <div className="text-xs text-zinc-500 max-w-[180px]">{price?.reason || "Prezzo non disponibile"}</div>
            )}
          </div>
        </div>
        {cfg.description && <p className="text-sm text-zinc-400 mt-4">{cfg.description}</p>}

        <div className="flex flex-wrap items-center gap-2 mt-4">
          {hasContract && (
            <>
              <button data-testid="raein-copy-contract" onClick={() => copy(cfg.contract)}
                className="inline-flex items-center gap-1.5 text-xs font-mono px-2.5 py-1.5 rounded-md bg-black/40 border border-white/10 text-zinc-300 hover:text-white transition-colors">
                <Copy size={12} /> {shortAddr(cfg.contract)}
              </button>
              <a href={cfg.bscscan_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-black/40 border border-white/10 text-zinc-300 hover:text-white transition-colors">
                BscScan <ExternalLink size={11} />
              </a>
            </>
          )}
          {isAdmin && (
            <button data-testid="raein-config-btn" onClick={() => setShowCfg((s) => !s)}
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-blue-500/15 border border-blue-500/20 text-blue-400 hover:bg-blue-500/25 transition-colors ml-auto">
              <Settings size={12} /> Configura
            </button>
          )}
        </div>
      </div>

      {/* Admin config */}
      {isAdmin && showCfg && (
        <div className="bg-[#141414] border border-blue-500/20 rounded-xl p-5 space-y-3" data-testid="raein-config-panel">
          <div className="text-sm font-semibold text-white">Configurazione token (Admin)</div>
          <div>
            <label className="text-xs text-zinc-500">Contract address (BSC)</label>
            <input data-testid="raein-cfg-contract" value={cfgForm.contract} placeholder="0x..."
              onChange={(e) => setCfgForm({ ...cfgForm, contract: e.target.value })}
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono text-white focus:border-blue-500/50 focus:outline-none" />
          </div>
          <div>
            <label className="text-xs text-zinc-500">Logo URL</label>
            <input data-testid="raein-cfg-logo" value={cfgForm.logo} placeholder="https://…/logo.png"
              onChange={(e) => setCfgForm({ ...cfgForm, logo: e.target.value })}
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-blue-500/50 focus:outline-none" />
          </div>
          <div>
            <label className="text-xs text-zinc-500">Descrizione</label>
            <textarea data-testid="raein-cfg-desc" value={cfgForm.description} rows={2}
              onChange={(e) => setCfgForm({ ...cfgForm, description: e.target.value })}
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-blue-500/50 focus:outline-none" />
          </div>
          <button data-testid="raein-cfg-save" onClick={saveCfg}
            className="px-4 py-2 rounded-md bg-blue-500 text-white text-sm font-semibold hover:bg-blue-400 transition-colors">
            Salva configurazione
          </button>
        </div>
      )}

      {!hasContract ? (
        <div className="bg-[#141414] border border-white/10 rounded-xl p-6 text-center text-sm text-zinc-500" data-testid="raein-not-configured">
          Il token non è ancora configurato. {isAdmin ? "Inserisci il contract address dalla sezione Configura." : "Torna presto!"}
        </div>
      ) : (
        <>
          {/* Wallet balances */}
          <div className="bg-[#141414] border border-white/10 rounded-xl p-6">
            {!address ? (
              <button data-testid="raein-connect-btn" onClick={() => setShowConnect(true)} disabled={connecting}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-amber-500 text-black text-sm font-bold hover:bg-amber-400 transition-colors disabled:opacity-50">
                <Wallet size={16} /> {connecting ? "Connessione…" : "Connect Wallet"}
              </button>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="font-mono text-zinc-300" data-testid="raein-address">{shortAddr(address)}</span>
                  </div>
                  <button onClick={() => refresh()} className="text-zinc-500 hover:text-white transition-colors" data-testid="raein-refresh">
                    <RefreshCw size={15} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-black/40 border border-white/10 rounded-lg p-4">
                    <div className="text-xs text-zinc-500">Saldo {cfg.symbol}</div>
                    <div className="text-lg font-bold text-white mt-1" data-testid="raein-token-balance">{fmt(tokenBal, 4)}</div>
                    {price?.available && tokenBal != null && (
                      <div className="text-xs text-zinc-500 mt-0.5">≈ ${fmt(Number(tokenBal) * Number(price.price_usd), 2)}</div>
                    )}
                  </div>
                  <div className="bg-black/40 border border-white/10 rounded-lg p-4">
                    <div className="text-xs text-zinc-500">Saldo BNB</div>
                    <div className="text-lg font-bold text-white mt-1" data-testid="raein-bnb-balance">{fmt(bnb, 5)}</div>
                  </div>
                </div>
                <button onClick={addToWallet} data-testid="raein-add-token"
                  className="w-full mt-3 inline-flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-md bg-black/40 border border-white/10 text-zinc-300 hover:text-white transition-colors">
                  <PlusCircle size={13} /> Aggiungi {cfg.symbol} al wallet
                </button>
              </>
            )}
          </div>

          {/* Buy / swap */}
          <div className="bg-[#141414] border border-white/10 rounded-xl p-6" data-testid="raein-swap">
            <div className="flex items-center gap-2 mb-4">
              <ArrowDownUp size={16} className="text-amber-400" />
              <h3 className="text-sm font-semibold text-white">Compra {cfg.symbol} con BNB</h3>
            </div>
            {noMarket && (
              <div className="mb-4 rounded-md bg-amber-500/10 border border-amber-500/20 p-3 text-xs text-amber-300" data-testid="raein-no-market">
                Trading non ancora disponibile: non esiste una <b>pool di liquidità</b> per {cfg.symbol} su PancakeSwap.
                {isAdmin && " Aggiungi liquidità (RAEIN + BNB) su PancakeSwap per attivare prezzo e acquisti."}
              </div>
            )}
            <label className="text-xs text-zinc-500">Importo in BNB</label>
            <input data-testid="raein-bnb-amount" value={bnbAmount} inputMode="decimal"
              onChange={(e) => setBnbAmount(e.target.value.replace(",", "."))}
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus:border-amber-500/50 focus:outline-none" />
            <div className="flex items-center justify-between mt-2 text-xs text-zinc-500">
              <span>Riceverai (stima)</span>
              <span className="font-mono text-zinc-300" data-testid="raein-quote">{quote ? `${fmt(quote, 2)} ${cfg.symbol}` : "—"}</span>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <label className="text-xs text-zinc-500">Slippage %</label>
              <input value={slippage} onChange={(e) => setSlippage(e.target.value.replace(",", "."))}
                className="w-16 bg-black/40 border border-white/10 rounded-md px-2 py-1 text-xs text-white focus:outline-none" data-testid="raein-slippage" />
            </div>
            <button data-testid="raein-buy-btn" onClick={buy} disabled={buying || !address || noMarket}
              className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-amber-500 text-black text-sm font-bold hover:bg-amber-400 transition-colors disabled:opacity-50">
              {buying ? "Elaborazione…" : noMarket ? "Trading non disponibile" : address ? `Compra ${cfg.symbol}` : "Collega il wallet per comprare"}
            </button>
            <a href={cfg.pancakeswap_url} target="_blank" rel="noopener noreferrer" data-testid="raein-pancakeswap-link"
              className="mt-3 inline-flex items-center justify-center gap-1.5 w-full text-xs text-zinc-400 hover:text-white transition-colors">
              Apri su PancakeSwap <ExternalLink size={11} />
            </a>
            <p className="text-[11px] text-zinc-600 mt-3 text-center">
              Lo swap avviene on-chain sulla BNB Smart Chain tramite PancakeSwap. La piattaforma non custodisce i tuoi fondi.
            </p>
          </div>
        </>
      )}

      <Dialog open={showConnect} onOpenChange={setShowConnect}>
        <DialogContent className="bg-[#141414] border-white/10 text-white sm:max-w-sm" data-testid="wallet-connect-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet size={18} className="text-amber-400" /> Connect Wallet
            </DialogTitle>
          </DialogHeader>
          <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mt-1">Top Wallets</div>
          <div className="grid grid-cols-2 gap-3 mt-2">
            {WALLETS.map((w) => (
              <button key={w.id} data-testid={`wallet-option-${w.id}`} disabled={connecting}
                onClick={() => connectWith(w.id)}
                className="flex flex-col items-center gap-3 p-5 rounded-xl bg-black/40 border border-white/10 hover:border-amber-500/50 hover:bg-white/5 transition-colors disabled:opacity-50">
                <img src={w.icon} alt={w.name} className="w-12 h-12 object-contain" />
                <span className="text-sm font-medium">{w.name}</span>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-zinc-600 mt-3 text-center">
            Su mobile verrai reindirizzato al browser interno del wallet. Su desktop serve l'estensione installata.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
